import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAppContext } from '../App';

const API = 'https://api.cpms-srlkevcharge.com';
const RULE_OPTIONS = ['Equal Sharing', 'First Come First Served', 'Departure Time'];

export default function LoadManagement() {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useAppContext();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configForm, setConfigForm] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);

  const configInitialized = React.useRef(false);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/load-management/${locationId}`);
      setData(res.data);
      if (!configInitialized.current) {
          setConfigForm({
          site_capacity_kw: String(res.data.config.site_capacity_kw ?? ''),
          building_load_kw: String(res.data.config.building_load_kw ?? ''),
          safety_margin_percent: String(res.data.config.safety_margin_percent ?? '10'),
          priority_rule_high: res.data.config.priority_rules.High,
          priority_rule_medium: res.data.config.priority_rules.Medium,
          priority_rule_low: res.data.config.priority_rules.Low,
        });
        configInitialized.current = true;
      }
    } catch (e) {
      console.error('Failed to fetch load management data', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); const t = setInterval(fetchData, 10000); return () => clearInterval(t); }, [locationId]);

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      await axios.patch(`${API}/load-management/${locationId}/config`, configForm);
      showToast('Load management settings updated');
      await fetchData();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to update settings', 'error');
    } finally {
      setSavingConfig(false);
    }
  };

  const handlePriorityChange = async (chargerId, priority) => {
    try {
      await axios.patch(`${API}/load-management/charger/${chargerId}/priority`, { priority });
      showToast('Charger priority updated');
      await fetchData();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to update priority', 'error');
    }
  };

  const handleDepartureTimeChange = async (sessionId, value) => {
    try {
      await axios.patch(`${API}/load-management/session/${sessionId}/departure-time`, {
        departure_time: value ? new Date(value).toISOString() : null
      });
      await fetchData();
    } catch (err) {
      showToast('Failed to update departure time', 'error');
    }
  };

  if (loading) return <div className="loading">Loading load management...</div>;
  if (!data) return <div className="loading">No data available</div>;

  const { summary, chargers, station_name } = data;
  const utilFillClass = summary.site_utilisation_percent > 90 ? 'high' : summary.site_utilisation_percent > 70 ? 'medium' : '';

  return (
    <>
      <button className="back-link" onClick={() => navigate(-1)}>← Back</button>

      <div className="page-header">
        <h1>Energy & Load Management</h1>
        <p>{station_name}</p>
      </div>

      <div className="lm-summary-grid">
        <div className="lm-summary-card">
          <div className="lm-summary-label">Site Capacity</div>
          <div className="lm-summary-value">{summary.site_capacity_kw}<span className="unit">kW</span></div>
        </div>
        <div className="lm-summary-card">
          <div className="lm-summary-label">Building Load</div>
          <div className="lm-summary-value">{summary.building_load_kw}<span className="unit">kW</span></div>
        </div>
        <div className="lm-summary-card">
          <div className="lm-summary-label">EV Charging Load</div>
          <div className="lm-summary-value">{summary.ev_charging_load_kw}<span className="unit">kW</span></div>
        </div>
        <div className="lm-summary-card">
          <div className="lm-summary-label">Available Capacity</div>
          <div className="lm-summary-value">{summary.available_capacity_kw}<span className="unit">kW</span></div>
        </div>
        <div className="lm-summary-card utilisation">
          <div className="lm-summary-label">Site Utilisation</div>
          <div className="lm-summary-value">{summary.site_utilisation_percent}<span className="unit">%</span></div>
          <div className="lm-utilisation-bar">
            <div className={`lm-utilisation-fill ${utilFillClass}`} style={{ width: `${Math.min(summary.site_utilisation_percent, 100)}%` }} />
          </div>
        </div>
      </div>

      {configForm && (
        <div className="lm-config-panel">
          <div className="lm-config-title">Site Configuration</div>
          <div className="lm-config-grid">
            <div className="form-group">
              <label>Site Capacity (kW)</label>
              <input type="text" value={configForm.site_capacity_kw} onChange={(e) => setConfigForm(prev => ({ ...prev, site_capacity_kw: e.target.value }))} placeholder="e.g. 250" />
            </div>
            <div className="form-group">
              <label>Current Building Load (kW)</label>
              <input type="text" value={configForm.building_load_kw} onChange={(e) => setConfigForm(prev => ({ ...prev, building_load_kw: e.target.value }))} placeholder="e.g. 134" />
            </div>
            <div className="form-group">
              <label>Safety Margin (%)</label>
              <input type="text" value={configForm.safety_margin_percent} onChange={(e) => setConfigForm(prev => ({ ...prev, safety_margin_percent: e.target.value }))} placeholder="e.g. 10" />
            </div>
          </div>

          <div className="lm-config-title">Priority Tiers — Secondary Allocation Rule</div>
          <div className="lm-priority-rules-grid">
            <div className="lm-priority-rule-card high">
              <div className="lm-priority-rule-label">High Priority</div>
              <select className="lm-priority-select" style={{ width: '100%' }} value={configForm.priority_rule_high} onChange={(e) => setConfigForm(prev => ({ ...prev, priority_rule_high: e.target.value }))}>
                {RULE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="lm-priority-rule-card medium">
              <div className="lm-priority-rule-label">Medium Priority</div>
              <select className="lm-priority-select" style={{ width: '100%' }} value={configForm.priority_rule_medium} onChange={(e) => setConfigForm(prev => ({ ...prev, priority_rule_medium: e.target.value }))}>
                {RULE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="lm-priority-rule-card low">
              <div className="lm-priority-rule-label">Low Priority</div>
              <select className="lm-priority-select" style={{ width: '100%' }} value={configForm.priority_rule_low} onChange={(e) => setConfigForm(prev => ({ ...prev, priority_rule_low: e.target.value }))}>
                {RULE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <button className="btn btn-green" onClick={handleSaveConfig} disabled={savingConfig}>
              {savingConfig ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <div className="section-header">
          <h2>Chargers</h2>
        </div>
        <div className="table-wrap">
          {chargers.length === 0 ? (
            <div className="empty-state">
              <h3>No chargers at this station</h3>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Charger</th>
                  <th>Rated Power</th>
                  <th>Current Power</th>
                  <th>Max Allowed</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Departure Time</th>
                </tr>
              </thead>
              <tbody>
                {chargers.map(c => (
                  <tr key={c.charger_id}>
                    <td style={{ fontWeight: 600, fontFamily: 'JetBrains Mono, monospace', fontSize: 12.5 }}>{c.ocpp_id}</td>
                    <td>{c.rated_power_kw} kW</td>
                    <td style={{ fontWeight: 600 }}>{c.current_power_kw} kW</td>
                    <td>{c.max_allowed_kw} kW</td>
                    <td>
                      <select
                        className="lm-priority-select"
                        value={c.priority}
                        onChange={(e) => handlePriorityChange(c.charger_id, e.target.value)}
                      >
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                      </select>
                    </td>
                    <td><span className={`badge badge-${c.status.toLowerCase()}`}>{c.status}</span></td>
                    <td>
                      {c.session_id ? (
                        <input
                          type="datetime-local"
                          defaultValue={c.departure_time ? c.departure_time.slice(0, 16) : ''}
                          onBlur={(e) => handleDepartureTimeChange(c.session_id, e.target.value)}
                          style={{ fontSize: 12, padding: '4px 6px', border: '1px solid var(--gray-200)', borderRadius: 6 }}
                        />
                      ) : (
                        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}